/**
 * Staff device authentication (client transport layer).
 *
 * Uses the EXISTING backend StaffDevice mechanism (see backend permissions.py):
 * a device presents a role-scoped token in the `X-Device-Token` header; the
 * server verifies its sha256 hash and role. This module is ONLY the client
 * transport/auth glue — it does not invent accounts, passwords or roles.
 *
 * The plaintext device token is provisioned out-of-band by an admin
 * (`StaffDevice.issue(name, role)`), entered once on the device, and stored in
 * localStorage as the device's own credential store. It is:
 *   - never placed in a URL,
 *   - never logged / sent to analytics,
 *   - only ever transmitted as the `X-Device-Token` request header,
 *   - never baked into source or the build.
 */
import axios from 'axios';

const STORAGE_KEY = 'staffDeviceToken';
export const STAFF_UNAUTHORIZED_EVENT = 'staff-device-unauthorized';

export function getDeviceToken() {
  try {
    return localStorage.getItem(STORAGE_KEY) || '';
  } catch (_e) {
    return '';
  }
}

export function setDeviceToken(token) {
  try {
    if (token) localStorage.setItem(STORAGE_KEY, token);
  } catch (_e) {
    /* storage unavailable — the request will simply fail auth and prompt again */
  }
}

export function clearDeviceToken() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (_e) {
    /* ignore */
  }
}

function notifyUnauthorized(reason) {
  clearDeviceToken();
  if (typeof window !== 'undefined' && window.dispatchEvent) {
    window.dispatchEvent(new CustomEvent(STAFF_UNAUTHORIZED_EVENT, { detail: { reason } }));
  }
}

/** True for the auth-failure statuses the device must re-authorize on. */
function isAuthFailure(status) {
  return status === 401 || status === 403;
}

/**
 * A dedicated axios instance for staff (reception) screens. Import it in place
 * of the global axios so every call carries the device token and reacts to
 * 401/403 centrally — without editing each call site.
 */
export const staffApi = axios.create();

// OPS-1: a reception signed in through /staff has a Django session cookie, so DRF enforces CSRF on
// writes. Without this header every POST/PATCH from these screens failed with 403 «CSRF token missing»
// (new customers, bracelet binding = visit, complete-order, enqueue) and the 403 also wiped the device token.
const UNSAFE = /^(post|put|patch|delete)$/i;
function csrfCookie() {
  if (typeof document === 'undefined') return '';
  const m = document.cookie.match('(^|;)\\s*csrftoken\\s*=\\s*([^;]+)');
  return m ? decodeURIComponent(m.pop()) : '';
}

staffApi.interceptors.request.use((config) => {
  const token = getDeviceToken();
  config.headers = config.headers || {};
  if (token) config.headers['X-Device-Token'] = token;
  const csrf = UNSAFE.test(config.method || '') ? csrfCookie() : '';
  if (csrf) config.headers['X-CSRFToken'] = csrf;
  return config;
});

staffApi.interceptors.response.use(
  (resp) => resp,
  (error) => {
    // Only an actual auth rejection clears the credential. A network error
    // (backend unavailable, no `response`) must NOT wipe a valid token.
    if (error && error.response && isAuthFailure(error.response.status)) {
      notifyUnauthorized(error.response.status === 403 ? 'forbidden' : 'unauthorized');
    }
    return Promise.reject(error);
  }
);

/**
 * fetch() wrapper for staff screens that use fetch (master tablets).
 * Injects the device token and raises the same unauthorized signal on 401/403.
 */
export async function staffFetch(input, init = {}) {
  const token = getDeviceToken();
  const headers = { ...(init.headers || {}) };
  if (token) headers['X-Device-Token'] = token;
  const csrf = UNSAFE.test(init.method || 'GET') ? csrfCookie() : '';
  if (csrf) headers['X-CSRFToken'] = csrf;
  const resp = await fetch(input, { ...init, headers });
  if (isAuthFailure(resp.status)) {
    notifyUnauthorized(resp.status === 403 ? 'forbidden' : 'unauthorized');
  }
  return resp;
}
